import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { action, computed } from '@ember/object';
import { differentFilters } from 'cio/utils/filters';
import timeframePropertyComputed from 'cio/utils/timeframe-computed';
import { task } from 'ember-concurrency';
import ActionModel, { ActionType, ExitType } from 'cio/models/action';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { copy } from 'ember-copy';
import { Edge } from 'cio/models/campaign';
import { edgesEndingInExitCount } from 'cio/utils/visual-workflow-builder/branching';
import { ConditionType } from 'cio/utils/condition-types';

const EDITABLE_PROPERTIES = ['conditions'];
const DIRTY_DEPENDENCIES = `action.{${EDITABLE_PROPERTIES.join(',')}}`;

export enum TimeDirection {
  On = 'on',
  Before = 'before',
  After = 'after',
}

abstract class IWaitUntilPath {
  action: ActionModel;

  abstract type: WaitUntilPathType;

  abstract isNew: boolean;
  abstract get isDirty(): boolean;

  abstract edge: Edge;
  abstract branchReconnectPoint: string;

  constructor(options: Record<string, any> & { action: ActionModel }) {
    this.action = options.action;
  }

  get isPathEmpty(): boolean {
    if (this.action.isPreBranchWaitUntil) {
      return true;
    }

    if (this.isNew) {
      return true;
    }

    if (this.branchReconnectPoint == null) {
      return this.edge.to == null;
    }

    return this.edge.to === this.branchReconnectPoint;
  }
}

export class ConditionPath extends IWaitUntilPath {
  type = WaitUntilPathType.Conditions;

  @tracked conditions: SerializedLegacyFilter[] = [];
  private originalConditions: SerializedLegacyFilter[] = [];

  edge: Edge;
  branchReconnectPoint: string;

  @tracked isNew = false;
  selectedConditionType?: ConditionType;

  constructor({
    action,
    conditions,
    isNew,
    selectedConditionType,
    edge,
    branchReconnectPoint,
  }: {
    action: ActionModel;
    conditions: SerializedLegacyFilter[];
    isNew?: boolean;
    selectedConditionType?: ConditionType;
    edge: Edge;
    branchReconnectPoint: string;
  }) {
    super({ action });

    this.conditions = copy(conditions, true);
    this.isNew = isNew ?? false;
    this.selectedConditionType = selectedConditionType ?? undefined;

    this.originalConditions = copy(conditions, true);

    this.edge = edge;
    this.branchReconnectPoint = branchReconnectPoint;
  }

  get isDirty() {
    return this.isNew || differentFilters(this.originalConditions, this.conditions);
  }
}

export class EventDelayPath extends IWaitUntilPath {
  type = WaitUntilPathType.EventDelay;

  @tracked delayEventAttr = '';
  private originalDelayEventAttr = '';

  @timeframePropertyComputed({
    countProperty: 'delayEventAttrOffsetCount',
    unitProperty: 'delayEventAttrOffsetUnit',
    defaultUnitWhenZero: 'day',
  })
  delayEventAttrOffset: number;
  private originalDelayEventAttrOffset: number;

  @tracked delayEventAttrOffsetCount = 0;
  @tracked delayEventAttrOffsetUnit = 'day';
  @tracked delayEventAttrOffsetTimeDirection: TimeDirection = TimeDirection.On;

  edge: Edge;
  branchReconnectPoint: string;

  @tracked isNew = false;

  constructor({
    action,
    delayEventAttr,
    delayEventAttrOffset,
    edge,
    branchReconnectPoint,
    isNew,
  }: {
    action: ActionModel;
    delayEventAttr: string;
    delayEventAttrOffset: number;
    edge: Edge;
    branchReconnectPoint: string;
    isNew?: boolean;
  }) {
    super({ action });

    this.delayEventAttr = delayEventAttr;
    this.originalDelayEventAttr = this.delayEventAttr;

    if (delayEventAttrOffset !== 0) {
      if (delayEventAttrOffset > 0) {
        this.delayEventAttrOffsetTimeDirection = TimeDirection.After;
      } else {
        this.delayEventAttrOffsetTimeDirection = TimeDirection.Before;
      }
    }

    this.delayEventAttrOffset = Math.abs(delayEventAttrOffset);
    this.originalDelayEventAttrOffset = delayEventAttrOffset;

    this.edge = edge;
    this.branchReconnectPoint = branchReconnectPoint;

    this.isNew = isNew ?? false;
  }

  get calculatedEventDelay() {
    let calculatedEventDelay = this.delayEventAttrOffset;
    if (this.delayEventAttrOffsetTimeDirection === TimeDirection.Before) {
      calculatedEventDelay = calculatedEventDelay * -1;
    }

    return calculatedEventDelay;
  }

  get isDirty() {
    let { calculatedEventDelay } = this;

    return (
      this.originalDelayEventAttr !== this.delayEventAttr || this.originalDelayEventAttrOffset !== calculatedEventDelay
    );
  }

  get hasMinimizedDelay() {
    if (this.isNew) {
      return !isEmpty(this.delayEventAttr);
    }

    return this.calculatedEventDelay < this.originalDelayEventAttrOffset;
  }
}

export class MaximumWaitPath extends IWaitUntilPath {
  type = WaitUntilPathType.MaxWait;

  @tracked exitType: ExitType;
  private originalExitType: ExitType;

  @timeframePropertyComputed({ countProperty: 'delayCount', unitProperty: 'delayUnit' }) delay: number;
  private originalDelay: number;

  edge: Edge;
  branchReconnectPoint: string;

  @tracked isNew = false;

  constructor({
    action,
    exitType,
    delay,
    edge,
    branchReconnectPoint,
    isNew,
  }: {
    action: ActionModel;
    exitType: ExitType;
    delay: number;
    edge: Edge;
    branchReconnectPoint: string;
    isNew?: boolean;
  }) {
    super({ action });

    this.exitType = exitType;
    this.originalExitType = this.exitType;
    this.delay = delay;
    this.originalDelay = this.delay;

    this.edge = edge;
    this.branchReconnectPoint = branchReconnectPoint;

    this.isNew = isNew ?? false;
  }

  @tracked delayCount = 0;
  @tracked delayUnit = 'minute';

  get isDirty() {
    let isExitTypeDirty = this.exitType !== this.originalExitType;
    let isDelayDirty = false;

    // ignore delay in dirty tracking for indefinite waits: it will be reset to "0" on save action if necessary
    if (this.exitType !== ExitType.Never) {
      isDelayDirty = this.delay !== this.originalDelay;
    }

    return isExitTypeDirty || isDelayDirty;
  }

  get hasMinimizedDelay() {
    // changing from "never" (indefinite wait) to other type will always mean smaller delay
    return (
      this.exitType !== ExitType.Never && (this.originalExitType === ExitType.Never || this.delay < this.originalDelay)
    );
  }
}

export enum WaitUntilPathType {
  Conditions = 'conditions',
  EventDelay = 'delay_event_attr',
  MaxWait = 'max_wait',
}

type WaitUntilPath = ConditionPath | EventDelayPath | MaximumWaitPath;

export default class WaitUntilAbstract extends BaseActionAbstract {
  type = 'wait_until';

  // BaseActionAbstract is an untyped EmberObject, and TS complains that
  // ActionModel can't be assigned to type "null"
  action!: ActionModel;
  actionType!: ActionType;

  _editableProperties: string[] = EDITABLE_PROPERTIES;
  _dirtyDependencies: string = DIRTY_DEPENDENCIES;

  @tracked conditions: SerializedLegacyFilter[] = [];
  @tracked conditionPaths: ConditionPath[] = [];
  get hasConditionPaths() {
    return this.conditionPaths.length > 0;
  }
  @tracked initialConditionPathsLength = 0;

  @tracked eventDelayPath: EventDelayPath | null = null;
  @tracked hasEventDelayPath = false;
  @tracked initialHasEventDelayPath = false;
  get eventDelayPathIndex() {
    return this.conditionPaths.length;
  }

  @tracked maximumWaitPath: MaximumWaitPath | null = null;
  @tracked hasMaximumWaitPath = false;
  @tracked initialHasMaximumWaitPath = false;
  get maximumWaitPathIndex() {
    return this.conditionPaths.length + (this.hasEventDelayPath ? 1 : 0);
  }

  get paths(): WaitUntilPath[] {
    let paths: WaitUntilPath[] = [...this.conditionPaths];

    if (this.hasEventDelayPath && this.eventDelayPath != null) {
      paths.push(this.eventDelayPath);
    }

    if (this.hasMaximumWaitPath && this.maximumWaitPath != null) {
      paths.push(this.maximumWaitPath);
    }

    return paths;
  }

  @action
  addConditionPath(path: { type: WaitUntilPathType; conditionType?: ConditionType }) {
    this.conditionPaths = [
      ...this.conditionPaths,
      new ConditionPath({
        action: this.action,
        conditions: [],
        isNew: true,
        selectedConditionType: path.conditionType,
        edge: {
          from: this.action.id,
          to: this.branchReconnectPoint,
          type: 'branch',
        },
        branchReconnectPoint: this.branchReconnectPoint,
      }),
    ];
  }

  @action
  deleteConditionPath(index: number) {
    this.conditionPaths = this.conditionPaths.filter((_, i) => i !== index);
  }

  @action
  addEventDelayPath() {
    if (this.hasEventDelayPath) {
      return;
    }

    this.hasEventDelayPath = true;
  }

  @action
  deleteEventDelayPath() {
    if (!this.hasEventDelayPath) {
      return;
    }

    this.hasEventDelayPath = false;
  }

  @action
  addMaximumWaitPath() {
    if (this.hasMaximumWaitPath) {
      return;
    }

    this.hasMaximumWaitPath = true;

    if (this.maximumWaitPath?.exitType === ExitType.Never) {
      this.maximumWaitPath.exitType = ExitType.Continue;
      this.maximumWaitPath.delayCount = 1;
    }
  }

  @action
  deleteMaximumWaitPath() {
    if (!this.hasMaximumWaitPath) {
      return;
    }

    this.hasMaximumWaitPath = false;
  }

  @action
  addPath(path: { type: WaitUntilPathType; conditionType?: ConditionType }) {
    switch (path.type) {
      case WaitUntilPathType.Conditions:
        this.addConditionPath(path);
        break;
      case WaitUntilPathType.EventDelay:
        this.addEventDelayPath();
        break;
      case WaitUntilPathType.MaxWait:
        this.addMaximumWaitPath();
        break;
    }
  }

  branchReconnectPoint = '';
  @tracked canDelete = true;
  @tracked canDeleteNotice = '';

  prepareForEdit() {
    super.prepareForEdit();
    let { action } = this;

    this.hasEventDelayPath = !isEmpty(action.delayEventAttr);
    this.initialHasEventDelayPath = this.hasEventDelayPath;
    this.hasMaximumWaitPath = action.exitType !== ExitType.Never;
    this.initialHasMaximumWaitPath = this.hasMaximumWaitPath;

    let actionEdges = this.action.campaign.edges.filter((edge) => edge.from === this.action.id);
    this.branchReconnectPoint = this.action.campaign.branchRejoinPointsMap[this.action.id];

    let eventDelayPathEdge: Edge | null = null;
    let maximumWaitPathEdge: Edge | null = null;

    // The maximum wait path is always last - if it exists, let's separate it
    // from the other edges
    if (this.hasMaximumWaitPath) {
      maximumWaitPathEdge = actionEdges.pop() as Edge;
    }
    // The event delay path is second-last if a maximum wait path exists, or
    // last if there is no max wait. This separates it from the other edges
    if (this.hasEventDelayPath) {
      eventDelayPathEdge = actionEdges.pop() as Edge;
    }

    if (action.isPreBranchWaitUntil) {
      let conditions = action.conditions ?? [];
      if (conditions.length > 0) {
        this.conditionPaths = [
          new ConditionPath({
            action: this.action,
            conditions,
            edge: actionEdges[0] as Edge,
            branchReconnectPoint: this.branchReconnectPoint,
          }),
        ];
      }
      this.initialConditionPathsLength = this.conditionPaths.length;
    } else {
      this.conditionPaths = (action.multiConditions ?? []).map(
        (c, i) =>
          new ConditionPath({
            action: this.action,
            conditions: c,
            edge: actionEdges[i] as Edge,
            branchReconnectPoint: this.branchReconnectPoint,
          }),
      );
      this.initialConditionPathsLength = this.conditionPaths.length;
    }

    this.eventDelayPath = new EventDelayPath({
      action: this.action,
      delayEventAttr: action.delayEventAttr,
      delayEventAttrOffset: action.delayEventAttrOffset,
      edge: eventDelayPathEdge ?? {
        from: action.id,
        to: this.branchReconnectPoint,
        type: 'branch',
      },
      branchReconnectPoint: this.branchReconnectPoint,
      isNew: !this.hasEventDelayPath,
    });

    this.maximumWaitPath = new MaximumWaitPath({
      action: this.action,
      exitType: action.exitType,
      delay: action.delay,
      edge: maximumWaitPathEdge ?? {
        from: action.id,
        to: this.branchReconnectPoint,
        type: 'branch',
      },
      branchReconnectPoint: this.branchReconnectPoint,
      isNew: !this.hasMaximumWaitPath,
    });

    let treeNode = this.action.campaign.branchesMap[this.action.id];

    if (treeNode != null) {
      this.canDelete = edgesEndingInExitCount(treeNode) !== treeNode.edges.length;

      if (!this.canDelete) {
        // If all edges of this branched wait until action end in exits, prompt
        // to reconnect the exits before deleting.
        // We currently don't support deleting branch actions that end in all
        // exists - users need to manually reconnect the paths.
        this.canDeleteNotice = 'Please reconnect all exits before deleting';
      }
    }
  }

  prepareForSave() {
    let action = super.prepareForSave();

    if (action.isPreBranchWaitUntil) {
      if (this.conditionPaths.length > 0) {
        action.set('conditions', this.conditionPaths[0].conditions);
      } else {
        action.set('conditions', []);
      }
    } else {
      action.set(
        'multiConditions',
        this.conditionPaths.map((p) => p.conditions),
      );
    }

    if (this.hasEventDelayPath && this.eventDelayPath != null) {
      action.set('delayEventAttr', this.eventDelayPath.delayEventAttr);
      action.set('delayEventAttrOffset', this.eventDelayPath.delayEventAttrOffset);

      if (this.eventDelayPath.delayEventAttrOffsetTimeDirection === TimeDirection.On) {
        action.set('delayEventAttrOffset', 0);
      }

      if (this.eventDelayPath.delayEventAttrOffsetTimeDirection === TimeDirection.Before) {
        action.set('delayEventAttrOffset', this.eventDelayPath.delayEventAttrOffset * -1);
      }
    } else {
      action.setProperties({
        delayEventAttr: '',
        delayEventAttrOffset: 0,
      });
    }

    if (this.hasMaximumWaitPath) {
      action.setProperties({
        exitType: this.maximumWaitPath?.exitType,
        delay: this.maximumWaitPath?.delay,
      });
    } else {
      action.setProperties({
        exitType: ExitType.Never,
        delay: 0,
      });
    }

    return action;
  }

  get areConditionPathsDirty() {
    let hasAddedOrRemovedConditions = this.conditionPaths.length !== this.initialConditionPathsLength;
    return hasAddedOrRemovedConditions || this.conditionPaths.some((p) => p.isDirty);
  }

  get isEventDelayConditionDirty() {
    let hasToggledConditions = this.hasEventDelayPath !== this.initialHasEventDelayPath;

    if (!this.hasEventDelayPath || this.eventDelayPath == null) {
      return hasToggledConditions;
    }

    return this.eventDelayPath.isDirty || hasToggledConditions;
  }

  get isMaximumWaitDirty() {
    let hasToggledMaximumWait = this.hasMaximumWaitPath !== this.initialHasMaximumWaitPath;

    if (!this.hasMaximumWaitPath || this.maximumWaitPath == null) {
      return hasToggledMaximumWait;
    }

    return this.maximumWaitPath.isDirty || hasToggledMaximumWait;
  }

  get arePathsDirty() {
    return this.areConditionPathsDirty || this.isEventDelayConditionDirty || this.isMaximumWaitDirty;
  }

  @computed(...EDITABLE_PROPERTIES.concat(DIRTY_DEPENDENCIES))
  get arePrimitiveConditionsDirty(): boolean {
    let { action } = this;

    let primitiveProperties = EDITABLE_PROPERTIES.filter((property) => !['conditions'].includes(property));

    return primitiveProperties.any((p) => (this as any)[p] !== (action as any)[p]);
  }

  get hasMinimizedDelay() {
    return (this.maximumWaitPath?.hasMinimizedDelay ?? false) || (this.eventDelayPath?.hasMinimizedDelay ?? false);
  }

  // @ts-ignore - incompatible typings
  get isDirty(): boolean {
    return (
      this.areConditionPathsDirty ||
      this.arePrimitiveConditionsDirty ||
      this.isEventDelayConditionDirty ||
      this.isMaximumWaitDirty
    );
  }

  @computed('maximumWaitPath.{delayCount,exitType}', 'hasEventDelayPath', 'eventDelayPath.delayEventAttr')
  get errors() {
    let { maximumWaitPath, eventDelayPath } = this;

    let delay = maximumWaitPath?.delayCount;

    if ((delay != null && isNaN(delay)) || typeof delay !== 'number') {
      return {
        delay: ['Delay must be a number'],
      };
    } else if (delay < 0) {
      return {
        delay: ['Time travel is not allowed'],
      };
    } else if (delay === 0 && maximumWaitPath?.exitType !== ExitType.Never) {
      return {
        delay: ['Must set a delay'],
      };
    } else if (this.hasEventDelayPath && isEmpty(eventDelayPath?.delayEventAttr)) {
      return {
        delayEventAttr: ['Must choose an event attribute'],
      };
    }
    return;
  }

  saveTask = task({ drop: true }, async () => {
    let action = this.prepareForSave();

    if (action.isPreBranchWaitUntil) {
      try {
        let response = await action.save();
        this.prepareForEdit();
        return response;
      } catch (e) {
        action.rollbackAttributes();
        throw e;
      }
    } else {
      let branchEdges: Edge[] = [];
      let otherEdges: Edge[] = [];
      this.action.campaign.consolidatedTree.edges.forEach((edge) => {
        if (edge.from === action.id) {
          branchEdges.push(edge);
        } else {
          otherEdges.push(edge);
        }
      });

      let hasNoReconnectPoint = false;
      let newEdges: Edge[] = [];
      let lastEdgeTarget = branchEdges[branchEdges.length - 1].to;

      if (this.paths.length === 0) {
        newEdges = [
          {
            from: action.id,
            to: lastEdgeTarget,
            type: 'continue',
          },
        ];
      } else {
        hasNoReconnectPoint = this.paths.some((path) => path.edge.to == null);

        // If this branch's paths all end in individual exits (no reconnect point
        // available for the branch), we first connect all new paths to the first
        // action of the "All others" path and save that to the API. Then we
        // issue another call to add individual exits for each of the new paths
        // and reconnect the edges to those.
        newEdges = this.paths.map((path, index) => ({
          from: path.edge.from,
          to: path.edge.to ?? lastEdgeTarget,
          type: path.edge.type,
          index,
        }));
      }

      try {
        let response = await this.action.campaign.saveAction({ action, edges: [...otherEdges, ...newEdges] });

        if (hasNoReconnectPoint) {
          newEdges = [];
          let exitActions: any[] = [];
          this.paths.forEach((path, index) => {
            let { edge } = path;
            let { to } = edge;
            if (edge.to == null) {
              to = `${-(exitActions.length + 1)}`;
              exitActions.push({
                id: to,
                type: 'exit_action',
              });
            }

            newEdges.push({
              from: edge.from,
              to,
              type: edge.type,
              index,
            });
          });

          await this.action.campaign.addActions({
            actions: exitActions,
            edges: [...otherEdges, ...newEdges],
            copyFromEnv: false,
          });
        }

        this.prepareForEdit();
        return response;
      } catch (e) {
        action.rollbackAttributes();
        throw e;
      }
    }
  });
}
